import React, { useMemo } from 'react';

const StatBadge = ({ label, value }) => (
  <span className="text-[9px] bg-[rgba(0,0,0,0.3)] border border-[#658d71] px-1.5 py-0.5 text-[#a9c9b1] font-bold uppercase tracking-wider">
    {label}: <span className="text-[#d8ceb3]">{value}</span>
  </span>
);

const PlayerCard = ({ player, type, onPlayerClick }) => {
  const name = player.person?.fullName || '--';
  const lastName = name.split(' ').slice(1).join(' ') || name;
  const personId = player.person?.id;
  const b = player.stats?.batting;
  const p = player.stats?.pitching;

  const badges =
    type === 'batting'
      ? [
          b?.hits > 0 && ['H', b.hits],
          b?.homeRuns > 0 && ['HR', b.homeRuns],
          b?.rbi > 0 && ['RBI', b.rbi],
          b?.runs > 0 && ['R', b.runs],
          b?.baseOnBalls > 0 && ['BB', b.baseOnBalls],
        ].filter(Boolean)
      : [
          p?.inningsPitched && ['IP', p.inningsPitched],
          p?.strikeOuts != null && ['K', p.strikeOuts],
          p?.earnedRuns != null && ['ER', p.earnedRuns],
          p?.baseOnBalls != null && ['BB', p.baseOnBalls],
        ].filter(Boolean);

  return (
    <div
      className="border border-[rgba(101,141,113,0.4)] p-2.5 bg-[rgba(0,0,0,0.1)] flex flex-col gap-1.5 cursor-pointer hover:border-[#a9c9b1] hover:bg-[rgba(255,255,255,0.04)] transition-all group"
      onClick={() => personId && onPlayerClick && onPlayerClick(personId, name)}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-black uppercase tracking-wide text-white truncate group-hover:text-[#d8ceb3] transition-colors">
          {lastName}
        </span>
        <div className="flex items-center gap-1.5 shrink-0 ml-2">
          <span className="text-[9px] text-[#658d71] uppercase font-bold tracking-widest">
            {type === 'batting' ? 'BAT' : 'PIT'}
          </span>
          <span className="text-[9px] text-[#658d71] group-hover:text-[#a9c9b1] transition-colors font-bold">→</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-1">
        {badges.map(([label, val]) => (
          <StatBadge key={label} label={label} value={val} />
        ))}
      </div>
    </div>
  );
};

const RecapTopPerformersClickable = ({ feed, onPlayerClick }) => {
  const performers = useMemo(() => {
    const boxscore = feed?.liveData?.boxscore;
    if (!boxscore) return { batters: [], pitchers: [] };

    const allPlayers = [];
    for (const side of ['away', 'home']) {
      const players = Object.values(boxscore.teams?.[side]?.players || {});
      allPlayers.push(...players);
    }

    const batters = allPlayers
      .filter(p => p.stats?.batting && (p.stats.batting.hits > 0 || p.stats.batting.rbi > 0 || p.stats.batting.homeRuns > 0))
      .sort((a, b) => {
        const scoreA = (a.stats.batting.hits || 0) * 2 + (a.stats.batting.homeRuns || 0) * 4 + (a.stats.batting.rbi || 0) * 2;
        const scoreB = (b.stats.batting.hits || 0) * 2 + (b.stats.batting.homeRuns || 0) * 4 + (b.stats.batting.rbi || 0) * 2;
        return scoreB - scoreA;
      })
      .slice(0, 6);

    const pitchers = allPlayers
      .filter(p => p.stats?.pitching && parseFloat(p.stats.pitching.inningsPitched) >= 1)
      .sort((a, b) => parseFloat(b.stats.pitching.inningsPitched) - parseFloat(a.stats.pitching.inningsPitched))
      .slice(0, 4);

    return { batters, pitchers };
  }, [feed]);

  if (!performers.batters.length && !performers.pitchers.length) {
    return (
      <div className="text-[#658d71] text-xs text-center py-6 uppercase tracking-widest">
        No performer data available
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[9px] text-[#658d71] tracking-widest">Click any player to view their career profile</p>
      {performers.batters.length > 0 && (
        <div>
          <p className="text-[9px] uppercase tracking-widest text-[#a9c9b1] font-bold mb-2">Top Batters</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {performers.batters.map((p, i) => (
              <PlayerCard key={i} player={p} type="batting" onPlayerClick={onPlayerClick} />
            ))}
          </div>
        </div>
      )}
      {performers.pitchers.length > 0 && (
        <div>
          <p className="text-[9px] uppercase tracking-widest text-[#a9c9b1] font-bold mb-2">Pitchers Used</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {performers.pitchers.map((p, i) => (
              <PlayerCard key={i} player={p} type="pitching" onPlayerClick={onPlayerClick} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RecapTopPerformersClickable;